import React, { useState } from "react";
import { Calendar as CalendarIcon, ChevronDown, X } from "lucide-react";
import { format, startOfDay, subDays, startOfMonth, startOfYear, endOfDay } from "date-fns";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const PRESETS = [
  { key: "today", label: "Hoy", get: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }) },
  { key: "7d", label: "7 d\u00edas", get: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) }) },
  { key: "30d", label: "30 d\u00edas", get: () => ({ from: startOfDay(subDays(new Date(), 29)), to: endOfDay(new Date()) }) },
  { key: "month", label: "Mes actual", get: () => ({ from: startOfMonth(new Date()), to: endOfDay(new Date()) }) },
  { key: "90d", label: "90 d\u00edas", get: () => ({ from: startOfDay(subDays(new Date(), 89)), to: endOfDay(new Date()) }) },
  { key: "year", label: "A\u00f1o", get: () => ({ from: startOfYear(new Date()), to: endOfDay(new Date()) }) },
];

export function DateRangeFilter({ value, onChange }) {
  // value: { from?: Date, to?: Date }
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value || {});

  const applyPreset = (preset) => {
    const range = preset.get();
    onChange(range);
    setDraft(range);
    setOpen(false);
  };

  const apply = () => {
    onChange(draft);
    setOpen(false);
  };

  const clear = () => {
    onChange({});
    setDraft({});
    setOpen(false);
  };

  const fmt = (d) => (d ? format(d, "dd MMM yyyy") : null);
  const label = value?.from || value?.to
    ? `${fmt(value.from) || "..."} \u2014 ${fmt(value.to) || "..."}`
    : "Todo el periodo";

  return (
    <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
      <div className="hidden md:flex items-center gap-1">
        {PRESETS.map((p) => (
          <Button
            key={p.key}
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-xs"
            data-testid={`date-preset-${p.key}`}
            onClick={() => applyPreset(p)}
          >
            {p.label}
          </Button>
        ))}
      </div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            data-testid="date-range-trigger"
            className={cn("h-10 sm:h-9 gap-2 font-normal w-full sm:w-auto")}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            <span className="text-xs truncate">{label}</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto max-w-[calc(100vw-1.5rem)] p-3" align="end" collisionPadding={12}>
          <div className="flex flex-col gap-3">
            <div className="md:hidden grid grid-cols-3 gap-1">
              {PRESETS.map((p) => (
                <Button key={p.key} size="sm" variant="ghost" className="h-9 text-xs" onClick={() => applyPreset(p)}>
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 max-h-[65vh] overflow-y-auto">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Desde</div>
                <Calendar
                  mode="single"
                  selected={draft.from}
                  onSelect={(d) => setDraft((s) => ({ ...s, from: d ? startOfDay(d) : undefined }))}
                  initialFocus
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Hasta</div>
                <Calendar
                  mode="single"
                  selected={draft.to}
                  onSelect={(d) => setDraft((s) => ({ ...s, to: d ? endOfDay(d) : undefined }))}
                />
              </div>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-border">
              <Button variant="ghost" size="sm" onClick={clear} data-testid="date-range-clear">
                <X className="h-3.5 w-3.5 mr-1" /> Limpiar
              </Button>
              <Button size="sm" onClick={apply} data-testid="date-range-apply">Aplicar</Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
